/*
 * @providesModule HeaderModal
 * 
 */

import * as Animatable from 'react-native-animatable';
import ConfettiView from 'ConfettiView';
import Label from 'Label';
import ModalNavigationController from 'ModalNavigationController';
import Optional from 'Optional';
import PrizeEmitter from 'PrizeEmitter';
import PropTypes from 'prop-types';
import React from 'react';
import Sizes from 'Sizes';
import styles from 'HeaderModalStyle';

import {
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

export const HEADER_MODAL_TEST_IDS = {
  HEADER:'header',
};

export default class HeaderModal extends React.Component {

  static propTypes = {
    title: PropTypes.string,
    titleTextStyleOverrides: PropTypes.any,
    headerStyleOverrides: PropTypes.any,
    children: PropTypes.node,
    onDone: PropTypes.func,
    renderInline: PropTypes.bool,
    showShadow: PropTypes.bool,
    style: PropTypes.any,
    displayConfettiFrontShadow: PropTypes.bool, // If component is animated, confetti will wait the duration of the animation before starting, regardless of if this is true or false.
    dismissOnShadowTap: PropTypes.bool,
    headerDecoration: PropTypes.node,

    // the following props affect the animation of the main content (the header and its children)
    isAnimated: PropTypes.bool,
    animationDuration: PropTypes.number,
    animationType: PropTypes.string,
    useNativeDriver: PropTypes.bool,
    easing: PropTypes.string,
    // If true, animate the background overlay with a fade in/fade out. Does nothing if renderInline is true.
    // Animations will affect the center content as they are contained within the background.
    animateBackground: PropTypes.bool,

    tierBadgeImage: PropTypes.string,

    testIdPrefix: PropTypes.string,
  };

  static defaultProps = {
    showShadow: true,
    isAnimated: false,
    dismissOnShadowTap: true,

  };

  constructor(props: unknown) {
    super(props);

    this.state = {
      opacity: 1,
      playConfetti: (this.props.displayConfettiFrontShadow && !this.props.isAnimated),
      prefix: props.testIdPrefix ? props.testIdPrefix : `header_modal`,
    };
    this.isAnimatingDismiss = false;
    this.isConfettiDisplayable = (this.props.displayConfettiFrontShadow && !this.props.isAnimated);
  }

  componentDidMount() {
    setTimeout(() => {
      if (this.props.displayConfettiFrontShadow && !this.state.playConfetti) {
        this.setState({playConfetti: true});
      }
      this.isConfettiDisplayable = true;
    }, this.props.animationDuration);
  }

  componentDidUpdate(prevProps: unknown, prevState: unknown) {
    if (this.props.displayConfettiFrontShadow != prevProps.displayConfettiFrontShadow) {
      if (this.props.displayConfettiFrontShadow && this.isConfettiDisplayable) {
        this.setState({playConfetti: true});
      } else if (!this.props.displayConfettiFrontShadow) {
        this.setState({playConfetti: false});
      }
    }
  }

  // Fade out and hide modal.
  // Will animate regardless of animateBackground prop
  async dismissWithAnimation(): Promise<void> {
    const fadeDuration = 300;

    if (!this.isAnimatingDismiss) {
      this.isAnimatingDismiss = true;
      return this._wrapperView?.animate('fadeOut', fadeDuration).then((complete: boolean) => {
        this.setState({opacity: 0}, () => {
          this.isAnimatingDismiss = false;
          ModalNavigationController.hideModal();
        });
      });
    }
  }

  _dismissModal = () => {
    if (!this.props.dismissOnShadowTap) {
      return;
    }

    if (this.props.onDone) {
      this.props.onDone();
    } else if (this.props.animateBackground) {
      this.dismissWithAnimation();
    } else {
      ModalNavigationController.hideModal();
    }
  }

  _renderHeader(): React.ReactNode {    
    return (
      <View testID={HEADER_MODAL_TEST_IDS.HEADER} style={[styles.header, this.props.headerStyleOverrides || {}]}>
        <Label style={[styles.title, this.props.titleTextStyleOverrides || {}]} 
               testID={`${this.state.prefix}_title`}>
          {this.props.title}
        </Label>
        <Optional test={this.props.headerDecoration}>
          {this.props.headerDecoration}
        </Optional>
      </View>
    );
  }

  _getWrapperAnimationProps(): Object {
    return (this.props.isAnimated ?
      {
        animation: this.props.animationType,
        useNativeDriver: this.props.useNativeDriver,
        easing: this.props.easing,
        duration: this.props.animationDuration,
      } : {}
    );
  }

  _getOverlayAnimationProps(): Object {
    return (this.props.animateBackground ?
      {
        animation: 'fadeIn',
        useNativeDriver: true,
        easing: 'ease-in-out',
        duration: 300,
      } : {}
    );
  }

  _renderModalContainer(): React.ReactNode {
    const containerStyle = Sizes.isLandscape() ? styles.containerLandscape : styles.container;
    const WrapperView = this.props.isAnimated ? Animatable.View : View;
    return (
      <WrapperView {...this._getWrapperAnimationProps()}
                   style={[containerStyle, this.props.style || {}]}
                   testID={this.props.testIdPrefix}>
        {this._renderHeader()}
        <View style={styles.childrenContainer}>
          {this.props.children}
        </View>
      </WrapperView>
    );
  }

  render(): React.ReactNode {
    if (this.props.renderInline) {
      return this._renderModalContainer();
    }
    const shadowOverlay = this.props.showShadow ? {backgroundColor: 'rgba(0,0,0,0.7)'} : {};
    return (
      <View style={[StyleSheet.absoluteFill, {opacity: this.state.opacity}]}>
        <Animatable.View style={StyleSheet.absoluteFill}
                         {...this._getOverlayAnimationProps()}
                         ref={(ref: React.ReactNode): void => this._wrapperView = ref}>
          <TouchableWithoutFeedback style={StyleSheet.absoluteFill}
                                    onPress={this._dismissModal}
                                    accessible={false}>
            <View style={[styles.modalOverlay, shadowOverlay]}>
              <Optional test={this.props.tierBadgeImage}>
                <PrizeEmitter style={[{backgroundColor:'transparent'}, StyleSheet.absoluteFill]}
                              amountWon={10}
                              isCashWin={false}
                              shouldShowPrizeRain={true}
                              shouldVibrate={false}
                              tierBadgeImage={this.props.tierBadgeImage} />
              </Optional>
              <ConfettiView style={[styles.modalOverlay, StyleSheet.absoluteFill]}
                            shouldPlay={this.state.playConfetti}>
                <TouchableWithoutFeedback style={styles.modalOverlay} accessible={false}>
                  {this._renderModalContainer()}
                </TouchableWithoutFeedback>
              </ConfettiView>
            </View>
          </TouchableWithoutFeedback>
        </Animatable.View>
      </View>
    );
  }
}
